<template>
  <section class="bg-white/80 backdrop-blur-md p-16 shadow-xl rounded-2xl border border-gray-100 transition-all duration-300 hover:shadow-2xl relative overflow-hidden group w-full mb-6">
    <div class="absolute -bottom-10 -right-10 w-48 h-48 bg-blue-50/80 rounded-full blur-2xl opacity-50 z-0 pointer-events-none"></div>

    <div class="relative z-10 flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
      <div class="flex items-center space-x-3">
        <h2 class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D84040] to-red-500">
          Artist Profile
        </h2>
        <span class="text-gray-400 font-medium text-base">( শিল্পীসকলক জানো আহক )</span>
      </div>
      <router-link to="/artists" class="hidden sm:inline-flex items-center space-x-2 text-[#D84040] font-semibold text-sm hover:text-red-600 transition-colors group/viewall">
        <span>View All</span>
        <svg class="w-4 h-4 transform group-hover/viewall:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
      </router-link>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-t-2 border-[#D84040]"></div>
    </div>

    <!-- Artists Grid Layout -->
    <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 relative z-10">
      <div
        v-for="artist in artists.slice(0, 5)"
        :key="artist.id"
        class="flex flex-col items-center gap-4 p-4 rounded-xl hover:bg-white/80 transition-all duration-300 gsap-artist border border-transparent hover:border-gray-100 shadow-sm hover:shadow-md text-center group/card"
      >
        <div class="relative w-28 h-28 flex-shrink-0 overflow-hidden shadow-sm group-hover/card:shadow text-center ring-4 ring-white transition-all duration-300 border border-gray-50">
          <img
            :src="artist.image"
            alt="Artist image"
            class="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110"
            @error="handleImageError"
          />
        </div>

        <div class="flex flex-col justify-center w-full">
          <h3 class="text-gray-800 font-bold leading-snug text-base mb-2 line-clamp-1 title-shadow group-hover/card:text-[#D84040] transition-colors">
            {{ artist.name }}
          </h3>

          <router-link
            :to="`/profile/${artist.id}`"
            class="inline-flex items-center justify-center space-x-1 text-gray-500 hover:text-red-500 transition-colors text-xs font-semibold uppercase tracking-wide group/read"
          >
            <span>Read More</span>
            <svg class="w-3.5 h-3.5 transform group-hover/read:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
          </router-link>
        </div>
      </div>
    </div>
    
    <div class="sm:hidden pt-6 mt-6 border-t border-gray-50 flex justify-center relative z-10" v-if="!loading && artists.length > 0">
      <router-link to="/artists" class="inline-flex justify-center items-center space-x-2 text-[#D84040] font-bold text-sm hover:text-red-600 transition-colors group/viewall px-6 py-2 bg-red-50 rounded-full w-full">
        <span>View All Artists</span>
        <svg class="w-4 h-4 transform group-hover/viewall:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
      </router-link>
    </div>

    <!-- Error -->
    <p v-if="artistError" class="text-red-500 font-medium text-sm mt-4 text-center bg-red-50 p-4 rounded-lg border border-red-100">
      {{ artistError }}
    </p>
    <div class="absolute -bottom-10 -right-10 w-48 h-48 bg-blue-50/80 rounded-full blur-2xl opacity-50 z-0 pointer-events-none"></div>

    <div class="relative z-10 flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
      <div class="flex items-center space-x-3">
        <h2 class="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D84040] to-red-500">
          Renowned Artist 
        </h2>
        <span class="text-gray-400 font-medium text-base">( স্বনামধন‍্য শিল্পী (দাপোণ গোষ্ঠীৰ দ্বাৰা প্ৰকাশিত) )</span>
      </div>
      <router-link to="/artists" class="hidden sm:inline-flex items-center space-x-2 text-[#D84040] font-semibold text-sm hover:text-red-600 transition-colors group/viewall">
        <span>View All</span>
        <svg class="w-4 h-4 transform group-hover/viewall:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
      </router-link>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-t-2 border-[#D84040]"></div>
    </div>

    <!-- Artists Grid Layout -->
    <div v-else class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6 relative z-10">
      <div
        v-for="artist in artists.slice(0, 5)"
        :key="artist.id"
        class="flex flex-col items-center gap-4 p-4 rounded-xl hover:bg-white/80 transition-all duration-300 gsap-artist border border-transparent hover:border-gray-100 shadow-sm hover:shadow-md text-center group/card"
      >
        <div class="relative w-28 h-28 flex-shrink-0 overflow-hidden shadow-sm group-hover/card:shadow text-center ring-4 ring-white transition-all duration-300 border border-gray-50">
          <img
            :src="artist.image"
            alt="Artist image"
            class="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-110"
            @error="handleImageError"
          />
        </div>

        <div class="flex flex-col justify-center w-full">
          <h3 class="text-gray-800 font-bold leading-snug text-base mb-2 line-clamp-1 title-shadow group-hover/card:text-[#D84040] transition-colors">
            {{ artist.name }}
          </h3>

          <router-link
            :to="`/profile/${artist.id}`"
            class="inline-flex items-center justify-center space-x-1 text-gray-500 hover:text-red-500 transition-colors text-xs font-semibold uppercase tracking-wide group/read"
          >
            <span>Read More</span>
            <svg class="w-3.5 h-3.5 transform group-hover/read:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
          </router-link>
        </div>
      </div>
    </div>
    
    <div class="sm:hidden pt-6 mt-6 border-t border-gray-50 flex justify-center relative z-10" v-if="!loading && artists.length > 0">
      <router-link to="/artists" class="inline-flex justify-center items-center space-x-2 text-[#D84040] font-bold text-sm hover:text-red-600 transition-colors group/viewall px-6 py-2 bg-red-50 rounded-full w-full">
        <span>View All Artists</span>
        <svg class="w-4 h-4 transform group-hover/viewall:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
      </router-link>
    </div>

    <!-- Error -->
    <p v-if="artistError" class="text-red-500 font-medium text-sm mt-4 text-center bg-red-50 p-4 rounded-lg border border-red-100">
      {{ artistError }}
    </p>
  </section>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue';
import gsap from "gsap";

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL;

const artists = ref([]);
const loading = ref(true);
const artistError = ref("");

const resolveImage = (img) => {
  if (!img) return `${FILE_BASE_URL}/images/default.png`;
  if (typeof img === "string" && img.startsWith("data:")) return img;
  if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) return img;
  if (img instanceof Blob) return URL.createObjectURL(img);
  if (typeof img === "string") return `${FILE_BASE_URL}${img}`;
  return `${FILE_BASE_URL}/images/default.png`;
};

const fetchArtists = async () => {
  const url = `${API_BASE_URL}/artists`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const approvedArtists = data.filter(a => a.is_approved);
    artists.value = approvedArtists.map((a) => ({
      ...a,
      image: resolveImage(a.image),
    }));
    artistError.value = "";
  } catch (err) {
    artistError.value = "Failed to load artists.";
  } finally {
    loading.value = false;
    nextTick(() => {
      gsap.from(".gsap-artist", {
        opacity: 0,
        y: 40,
        scale: 0.95,
        duration: 0.8,
        stagger: 0.1,
        ease: "power2.out",
      });
    });
  }
};

const handleImageError = (e) => {
  const img = e.target;
  if (img.src.includes("default.png")) {
    img.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";
    return;
  }
  img.src = `${FILE_BASE_URL}/images/default.png`;
  img.onerror = null;
};

onMounted(() => {
  fetchArtists();
});
</script>
